import { z } from 'zod'
import { sponsorChildProfileSchema, sponsorMilestoneSchema } from './sponsor-adopt.schema'

// Spec PWA-001/PWA-002 — App do Padrinho: contrato da API /api/v1/sponsor/*. FONTE DA VERDADE (desde 2026-10-01,
// PWA-002 Clarification #2): este diretorio, usado pelo servidor (server/) e pelas telas. So pode importar zod e ./sponsor*.
// Regra: schemas de SAIDA que carregam dado de crianca sao .strict() — campo extra quebra parse (FR-020).

// ─── Primitivos ───────────────────────────────

export const SPONSOR_LOCALES = ['pt-BR', 'en-US', 'es-ES', 'de-DE', 'ja-JP'] as const
export const sponsorLocaleSchema = z.enum(SPONSOR_LOCALES)
export type SponsorLocale = z.infer<typeof sponsorLocaleSchema>

export const sponsorOtpChannelSchema = z.enum(['whatsapp', 'email'])
export type SponsorOtpChannel = z.infer<typeof sponsorOtpChannelSchema>

export const sponsorSessionTransportSchema = z.enum(['cookie', 'bearer'])
export type SponsorSessionTransport = z.infer<typeof sponsorSessionTransportSchema>

export const sponsorClientSchema = z.enum(['web', 'native'])
export type SponsorClient = z.infer<typeof sponsorClientSchema>

export const sponsorDisplayModeSchema = z.enum(['browser', 'standalone'])
export type SponsorDisplayMode = z.infer<typeof sponsorDisplayModeSchema>

const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Cor deve estar no formato #RRGGBB')
const httpsUrlSchema = z.string().url().refine((u) => u.startsWith('https://'), 'URL deve usar HTTPS')

// childRef = base64url(HMAC)[0..22] — opaco, por tenant (D-07)
export const sponsorChildRefSchema = z.string().regex(/^[A-Za-z0-9_-]{22}$/)

// Token de link identificado / sessao: 32 bytes em base64url sem padding = 43 chars
export const sponsorOpaqueTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/)

// ─── Codigos de erro do dominio sponsor ───────────────────────────────

export const SPONSOR_ERROR_CODES = [
    'SPONSOR_APP_UNAVAILABLE',
    'SPONSOR_OTP_THROTTLED',
    'SPONSOR_OTP_INVALID',
    'SPONSOR_THROTTLE_UNAVAILABLE',
    'SPONSOR_CRM_UNAVAILABLE',
    'SPONSOR_DATA_UNAVAILABLE',
    'SPONSOR_IDENTITY_AMBIGUOUS',
    'SPONSOR_LINK_INVALID',
    'SPONSOR_SESSION_REQUIRED',
    'SPONSOR_CLIENT_HEADER_REQUIRED',
    'SPONSOR_NOT_FOUND',
    'SPONSOR_MEDIA_EXPIRED',
    'SPONSOR_THEME_CONTRAST',
    'SPONSOR_VALIDATION_ERROR',
    // Acesso com senha (telas 02/03)
    'SPONSOR_INVALID_CREDENTIALS',
    'SPONSOR_ACCOUNT_LOCKED',
    'SPONSOR_ACCOUNT_EXISTS',
    'SPONSOR_NOT_FOUND_IN_CRM',
    'SPONSOR_WEAK_PASSWORD',
    // Spec PWA-031: nova senha igual a atual
    'SPONSOR_SAME_PASSWORD',
    // Doacao, visita e campanha
    'SPONSOR_PAYMENT_DECLINED',
    // Spec PWA-019: alterar um apadrinhamento do app ja cancelado
    'SPONSOR_SPONSORSHIP_CANCELED',
    'SPONSOR_VISIT_INVALID_DATES',
    'SPONSOR_CAMPAIGN_FULL',
    // Spec PWA-005: acao ainda sem fonte real, bloqueada quando a tela mostra dados reais
    'SPONSOR_FEATURE_UNAVAILABLE',
    // Spec PWA-007: e-mail ja e o login de outra conta
    'SPONSOR_CONTACT_IN_USE',
    // Spec PWA-013: foto ou audio fora das regras (reason: type | size | count); armazenamento nao configurado
    'SPONSOR_ATTACHMENT_INVALID',
    'SPONSOR_MEDIA_UNAVAILABLE',
    // Spec PWA-014: avisos neste aparelho — desligados no servidor; aparelho nao inscrito; inscricao morta; impersonacao
    'SPONSOR_PUSH_UNAVAILABLE',
    'SPONSOR_PUSH_NOT_FOUND',
    'SPONSOR_PUSH_GONE',
    'SPONSOR_PUSH_OWN_ACCOUNT',
    // Spec PWA-018: transcricao desligada no servidor (sem a chave)
    'SPONSOR_TRANSCRIPTION_UNAVAILABLE',
    // PWA-020: traducao desligada no servidor (sem a chave)
    'SPONSOR_TRANSLATION_UNAVAILABLE',
    // Spec PWA-015: crianca reservada/apadrinhada por outra pessoa; reserva venceu antes de pagar
    'SPONSOR_CHILD_TAKEN',
    'SPONSOR_RESERVATION_EXPIRED',
    // Spec PWA-022: Braspag fora do ar ou credencial recusada (AccessToken do cartao)
    'SPONSOR_PAYMENTS_UNAVAILABLE',
] as const
export const sponsorErrorCodeSchema = z.enum(SPONSOR_ERROR_CODES)
export type SponsorErrorCode = z.infer<typeof sponsorErrorCodeSchema>

// ─── Auth: pedido e validacao de codigo (E3, E4, E5) ───────────────────────────────

export const sponsorOtpRequestSchema = z.object({
    channel: sponsorOtpChannelSchema,
    destination: z.string().trim().min(3).max(254),
}).strict()
export type SponsorOtpRequestInput = z.infer<typeof sponsorOtpRequestSchema>

export const sponsorOtpRequestResponseSchema = z.object({
    challengeId: z.string().uuid(),
    maskedDestination: z.string().min(1).max(64),
    resendAfterSeconds: z.number().int().positive(),
    expiresInSeconds: z.number().int().positive(),
}).strict()
export type SponsorOtpRequestResponse = z.infer<typeof sponsorOtpRequestResponseSchema>

export const sponsorOtpVerifySchema = z.object({
    challengeId: z.string().uuid(),
    code: z.string().regex(/^\d{6}$/, 'Codigo deve ter 6 digitos'),
    transport: sponsorSessionTransportSchema.default('cookie'),
}).strict()
export type SponsorOtpVerifyInput = z.infer<typeof sponsorOtpVerifySchema>

export const sponsorLinkRedeemSchema = z.object({
    token: sponsorOpaqueTokenSchema,
}).strict()
export type SponsorLinkRedeemInput = z.infer<typeof sponsorLinkRedeemSchema>

// ─── Identidade / perfil (E4, E8, E9) ───────────────────────────────

export const sponsorIdentityViewSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string().min(1).max(120),
    locale: sponsorLocaleSchema,
}).strict()
export type SponsorIdentityView = z.infer<typeof sponsorIdentityViewSchema>

export const sponsorOtpVerifyResponseSchema = z.object({
    identity: sponsorIdentityViewSchema,
    // Presente apenas quando transport=bearer e nativeClientsEnabled (D-02)
    token: sponsorOpaqueTokenSchema.optional(),
}).strict()
export type SponsorOtpVerifyResponse = z.infer<typeof sponsorOtpVerifyResponseSchema>

export const sponsorMaskedContactSchema = z.object({
    type: z.enum(['phone', 'email']),
    masked: z.string().min(1).max(64),
}).strict()

// Genero (para a concordancia dos textos: madrinha/padrinho, apadrinhada/apadrinhado). null = texto neutro.
export const sponsorGenderSchema = z.enum(['female', 'male', 'other']).nullable()
export type SponsorGender = z.infer<typeof sponsorGenderSchema>

// Spec PWA-004 — situacao do apadrinhamento: com crianca; aguardando o vinculo (acabou de apadrinhar);
// nenhum (ex-padrinho ou doador que nunca apadrinhou).
export const sponsorshipStatusSchema = z.enum(['active', 'awaiting', 'none'])
export type SponsorshipStatus = z.infer<typeof sponsorshipStatusSchema>

export const sponsorMeSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string().min(1).max(120),
    locale: sponsorLocaleSchema,
    contacts: z.array(sponsorMaskedContactSchema),
    hasActiveSponsorship: z.boolean(),
    sponsorshipStatus: sponsorshipStatusSchema,
    gender: sponsorGenderSchema,
    /** Visualização do atendimento (painel "Ver como padrinho"): o app mostra a faixa "somente leitura". Ausente = o
        próprio padrinho. */
    staffView: z.literal(true).optional(),
}).strict()
export type SponsorMe = z.infer<typeof sponsorMeSchema>

// Preferencias do padrinho (tela 18 · Configuracoes). PATCH aceita parcial; GET devolve completo.
export const sponsorNotificationPrefsSchema = z.object({
    childMessages: z.boolean(),
    notices: z.boolean(),
    campaigns: z.boolean(),
    billing: z.boolean(),
}).strict()

export const sponsorPreferencesViewSchema = z.object({
    locale: sponsorLocaleSchema,
    // RN-20/RN-36: traducao automatica das mensagens recebidas para o idioma escolhido
    autoTranslate: z.boolean(),
    notifications: sponsorNotificationPrefsSchema,
    // PWA-020 FR-006: false = traducao desligada no servidor (sem chave); ausente = versao anterior do servidor
    translationAvailable: z.boolean().optional(),
}).strict()
export type SponsorPreferencesView = z.infer<typeof sponsorPreferencesViewSchema>

export const sponsorPreferencesSchema = z.object({
    locale: sponsorLocaleSchema.optional(),
    autoTranslate: z.boolean().optional(),
    notifications: sponsorNotificationPrefsSchema.partial().strict().optional(),
}).strict().refine(v => Object.keys(v).length > 0, 'Informe ao menos uma preferencia')
export type SponsorPreferencesInput = z.infer<typeof sponsorPreferencesSchema>

// ─── Traducao automatica do conteudo (Spec PWA-020 D-04) ───────────────────────────────
// Vai no envelope, ao lado de `data` ({ data, translations }): cada texto original que a tela mostra e a traducao dele
// para o idioma da tela. O texto original continua em `data` ("Ver original" sem outra chamada).

export const sponsorTranslationEntrySchema = z.object({
    source: z.string().min(1).max(10_000),
    text: z.string().min(1).max(20_000),
    // Idioma do original (o conteudo das criancas, da OSP e da equipe e pt-BR)
    from: z.string().min(2).max(10),
}).strict()
export type SponsorTranslationEntry = z.infer<typeof sponsorTranslationEntrySchema>

export const sponsorTranslationsSchema = z.object({
    to: sponsorLocaleSchema,
    items: z.array(sponsorTranslationEntrySchema).max(500),
}).strict()
export type SponsorTranslations = z.infer<typeof sponsorTranslationsSchema>

// PWA-020 D-07: "Traduzir" sob demanda (interruptor desligado): uma mensagem recebida ou a transcricao de um audio da crianca
export const sponsorTranslateRequestSchema = z.union([
    z.object({ messageId: z.string().uuid() }).strict(),
    z.object({ attachmentId: z.string().uuid() }).strict(),
])
export type SponsorTranslateRequest = z.infer<typeof sponsorTranslateRequestSchema>

// ─── Bootstrap publico (E1) — sem dado pessoal, sem config interna ───────────────────────────────

export const sponsorBrandingViewSchema = z.object({
    shortName: z.string().min(1).max(12),
    themeColor: hexColorSchema,
    // Cor de destaque (CTA secundario, marcadores) — ex.: amarelo ChildFund #FBB21D
    accentColor: hexColorSchema,
    backgroundColor: hexColorSchema,
    // Logo para fundo claro
    logoUrl: z.string().url().nullable(),
    // Logo para fundo na cor da marca (cabecalhos)
    logoInverseUrl: z.string().url().nullable(),
    // Textura decorativa sobre a cor da marca, repetida em mosaico
    patternUrl: z.string().url().nullable(),
    // Simbolo da organizacao (avatar de publicacoes e conversas institucionais)
    markUrl: z.string().url().nullable(),
    // Foto de boas-vindas do onboarding (conteudo do tenant, RN-01); pessoas retratadas com autorizacao de imagem
    welcomeImageUrl: z.string().url().nullable(),
}).strict()

export const sponsorBootstrapSchema = z.object({
    appName: z.string().min(1).max(45),
    branding: sponsorBrandingViewSchema,
    channels: z.object({ whatsapp: z.boolean(), email: z.boolean() }).strict(),
    legal: z.object({
        privacyNoticeUrl: httpsUrlSchema.nullable(),
        dpoContact: z.string().max(254).nullable(),
    }).strict(),
    supportedLocales: z.array(sponsorLocaleSchema).min(1),
}).strict()
export type SponsorBootstrap = z.infer<typeof sponsorBootstrapSchema>

// ─── Projecao de crianca (E10, E11) — lista fechada, FR-020 / RN-15 ───────────────────────────────
// RN-15: so primeiro nome, idade, cidade/UF e OSP. Sobrenome, escola, endereco e data de nascimento exata nunca.

export const sponsorChildViewSchema = z.object({
    childRef: sponsorChildRefSchema,
    firstName: z.string().min(1).max(40),
    gender: sponsorGenderSchema,
    ageYears: z.number().int().min(0).max(25),
    region: z.object({
        city: z.string().min(1).max(80),
        state: z.string().length(2),
        country: z.string().length(2),
    }).strict(),
    // Organizacao Social Parceira que acompanha a crianca
    osp: z.object({ name: z.string().min(1).max(80) }).strict().nullable(),
    sponsorship: z.object({
        status: z.literal('active'),
        since: z.string().date(),
    }).strict(),
    media: z.object({
        photoUrl: z.string().url(),
        expiresAt: z.string().datetime(),
    }).strict().nullable(),
    unreadMessages: z.number().int().min(0),
    // Spec PWA-015: de onde veio o apadrinhamento (Dynamics ou app) e os marcos mais recentes (Novidades)
    origin: z.enum(['crm', 'app']),
    recentMilestones: z.array(z.object({
        id: z.string().uuid(),
        title: z.string().min(1).max(80),
        kind: z.enum(['school', 'sports', 'arts', 'personal', 'other']),
        approvedAt: z.string().datetime(),
    }).strict()).max(3),
}).strict()
export type SponsorChildView = z.infer<typeof sponsorChildViewSchema>

// RN-17: conquistas lancadas pela equipe de campo; a API devolve no maximo as 3 ultimas.
export const sponsorChildAchievementSchema = z.object({
    title: z.string().min(1).max(80),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    kind: z.enum(['education', 'project', 'highlight']),
}).strict()
export type SponsorChildAchievement = z.infer<typeof sponsorChildAchievementSchema>

/** Spec PWA-026: texto da ficha CIF (paragrafos separados por quebra de linha) e o dia da ultima alteracao. */
export const sponsorChildStorySchema = z.object({
    text: z.string().min(1).max(4000),
    updatedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
}).strict()
export type SponsorChildStory = z.infer<typeof sponsorChildStorySchema>

export const sponsorChildDetailSchema = sponsorChildViewSchema.extend({
    sponsorshipDurationMonths: z.number().int().min(0),
    // PWA-024: codigo da crianca no ChildFund (o que o padrinho cita nas cartas); null nas criancas do app
    code: z.string().regex(/^[\w-]{1,20}$/).nullable(),
    // Frase da propria crianca, registrada com o guardiao pela equipe de campo (opcional)
    aspiration: z.string().min(1).max(160).nullable(),
    interests: z.array(z.string().min(1).max(30)).max(8),
    achievements: z.array(sponsorChildAchievementSchema).max(3),
    // Spec PWA-015: perfil (so criancas do app) e marcos aprovados (app e Dynamics)
    profile: sponsorChildProfileSchema.nullable(),
    milestones: z.array(sponsorMilestoneSchema).max(50),
    // Spec PWA-026: historia da crianca (ficha CIF do CRM), ja protegida no servidor; null nas criancas do app
    story: sponsorChildStorySchema.nullable(),
}).strict()
export type SponsorChildDetail = z.infer<typeof sponsorChildDetailSchema>

// ─── Comunicados (tela 05 · RN-10) ───────────────────────────────

export const sponsorNoticeSchema = z.object({
    id: z.string().uuid(),
    // PWA-016: 'milestone' = marco aprovado na vida de uma crianca do padrinho
    // PWA-027: 'ritual' = aniversario do vinculo, Natal ou mes do aniversario da crianca (convite para escrever)
    kind: z.enum(['message', 'milestone', 'ritual', 'campaign', 'moderation', 'billing', 'report', 'info']),
    title: z.string().min(1).max(120),
    body: z.string().min(1).max(400),
    createdAt: z.string().datetime(),
    read: z.boolean(),
    // Destino dentro do proprio app (nunca URL externa arbitraria)
    target: z.object({
        // PWA-019: 'sponsorship' = tela do apadrinhamento feito pelo app (id = protocolo)
        // PWA-029: 'event' = pop-up de eventos da Inicio aberto no evento (id = campaignid)
        kind: z.enum(['thread', 'child', 'sponsorship', 'event', 'none']),
        id: z.string().max(64).nullable(),
    }).strict(),
}).strict()
export type SponsorNotice = z.infer<typeof sponsorNoticeSchema>

export const sponsorNoticeListSchema = z.object({
    items: z.array(sponsorNoticeSchema),
    unreadCount: z.number().int().min(0),
}).strict()
export type SponsorNoticeList = z.infer<typeof sponsorNoticeListSchema>

// ─── Novidades das minhas criancas (Spec PWA-017) ───────────────────────────────

/** Publicacoes por pagina no feed do Inicio. */
export const SPONSOR_UPDATES_PAGE = 5

/** Mensagem recebida de uma crianca ou marco aprovado na vida dela, com as fotos (links temporarios). */
export const sponsorUpdateSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(['message', 'milestone']),
    at: z.string().datetime(),
    child: z.object({ firstName: z.string().min(1).max(40), childRef: sponsorChildRefSchema.nullable() }).strict(),
    // Titulo do marco (mensagem: null)
    title: z.string().min(1).max(80).nullable(),
    text: z.string().max(500),
    photos: z.array(z.object({ url: z.string().url(), expiresAt: z.string().datetime() }).strict()).max(3),
    audio: z.boolean(),
    // Mensagem que o padrinho ainda nao leu na conversa
    unread: z.boolean(),
    target: z.object({ kind: z.enum(['thread', 'child']), id: z.string().min(1).max(64) }).strict(),
}).strict()
export type SponsorUpdate = z.infer<typeof sponsorUpdateSchema>

export const sponsorUpdatesPageSchema = z.object({
    items: z.array(sponsorUpdateSchema).max(SPONSOR_UPDATES_PAGE),
    next: z.string().max(200).nullable(),
}).strict()
export type SponsorUpdatesPage = z.infer<typeof sponsorUpdatesPageSchema>

// ─── Linha do tempo do apadrinhamento e rituais (Spec PWA-027) ───────────────────────────────

/** Itens por pagina da linha do tempo de uma crianca. */
export const SPONSOR_TIMELINE_PAGE = 20

const timelinePhotoSchema = z.object({ url: z.string().url(), expiresAt: z.string().datetime() }).strict()
const timelineBase = { id: z.string().uuid(), at: z.string().datetime() }
const timelineThreadId = z.string().min(1).max(64)

/** D-03: inicio, aniversarios do vinculo, mensagens (aprovadas), marcos e a historia (CIF) atualizada.
    Inicio, aniversario e historia: `at` = o dia as 12h UTC (a tela mostra so a data). */
export const sponsorTimelineItemSchema = z.discriminatedUnion('kind', [
    z.object({ ...timelineBase, kind: z.literal('start') }).strict(),
    z.object({ ...timelineBase, kind: z.literal('anniversary'), years: z.number().int().min(1).max(60) }).strict(),
    z.object({
        ...timelineBase, kind: z.literal('message_received'),
        text: z.string().max(160), photos: z.array(timelinePhotoSchema).max(3), audio: z.boolean(), threadId: timelineThreadId,
    }).strict(),
    z.object({
        ...timelineBase, kind: z.literal('message_sent'),
        text: z.string().max(160), photos: z.array(timelinePhotoSchema).max(3), audio: z.boolean(), threadId: timelineThreadId,
    }).strict(),
    z.object({
        ...timelineBase, kind: z.literal('milestone'),
        title: z.string().min(1).max(80), text: z.string().max(500), photos: z.array(timelinePhotoSchema).max(1),
    }).strict(),
    z.object({ ...timelineBase, kind: z.literal('story_updated') }).strict(),
])
export type SponsorTimelineItem = z.infer<typeof sponsorTimelineItemSchema>

export const sponsorTimelinePageSchema = z.object({
    items: z.array(sponsorTimelineItemSchema).max(SPONSOR_TIMELINE_PAGE),
    next: z.string().max(200).nullable(),
    // D-01: so a data real do CRM (sem data, sem inicio nem aniversarios)
    sponsorSince: z.string().date().nullable(),
}).strict()
export type SponsorTimelinePage = z.infer<typeof sponsorTimelinePageSchema>

export const SPONSOR_RITUAL_KINDS = ['anniversary', 'christmas', 'birthday_month'] as const

/** D-05/D-06: ritual ativo hoje para uma crianca do padrinho (cartao da Inicio). D-04: nunca o dia do aniversario. */
export const sponsorRitualSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(SPONSOR_RITUAL_KINDS),
    childRef: sponsorChildRefSchema,
    firstName: z.string().min(1).max(40),
    // Anos completos do apadrinhamento (so 'anniversary')
    years: z.number().int().min(1).max(60).nullable(),
    threadId: timelineThreadId,
}).strict()
export type SponsorRitual = z.infer<typeof sponsorRitualSchema>

export const sponsorRitualListSchema = z.object({ items: z.array(sponsorRitualSchema).max(60) }).strict()
export type SponsorRitualList = z.infer<typeof sponsorRitualListSchema>

// ─── Caixa de mensagens (tela 08 · RN-19) — PWA-009: escrever, moderacao do ChildFund e entrega pela OSP ─────────

// PWA-009: approved = aprovada, aguardando a OSP entregar; canceled = o apadrinhamento terminou antes da entrega
export const sponsorMessageStatusSchema = z.enum(['in_review', 'approved', 'delivered', 'rejected', 'canceled'])
export type SponsorMessageStatus = z.infer<typeof sponsorMessageStatusSchema>

export const sponsorThreadSummarySchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(['child', 'osp', 'organization']),
    title: z.string().min(1).max(80),
    childRef: sponsorChildRefSchema.nullable(),
    // PWA-009: null = conversa ainda sem mensagens (toda crianca apadrinhada tem conversa)
    lastMessage: z.object({
        preview: z.string().min(1).max(160),
        at: z.string().datetime(),
        direction: z.enum(['in', 'out']),
        status: sponsorMessageStatusSchema,
    }).strict().nullable(),
    unreadCount: z.number().int().min(0),
    // PWA-010: a ultima mensagem RECEBIDA da crianca ("Novidades das minhas criancas" no Inicio)
    lastReceived: z.object({
        preview: z.string().min(1).max(160),
        at: z.string().datetime(),
        // PWA-011: o que veio junto ("Foto" / "Audio" na previa)
        photos: z.number().int().min(0).max(3),
        audio: z.boolean(),
    }).strict().nullable(),
    // PWA-009: false quando o apadrinhamento terminou (conversa so para leitura) ou para conversas da OSP/ChildFund
    canWrite: z.boolean(),
}).strict()
export type SponsorThreadSummary = z.infer<typeof sponsorThreadSummarySchema>

export const sponsorThreadMessageSchema = z.object({
    id: z.string().uuid(),
    direction: z.enum(['in', 'out']),
    body: z.string().min(1).max(2000),
    sentAt: z.string().datetime(),
    status: sponsorMessageStatusSchema,
    // RN-22: recusa sempre traz motivo em linguagem simples e como reescrever
    rejection: z.object({
        reason: z.string().min(1).max(400),
        guidance: z.string().min(1).max(400),
        reviewedBy: z.string().min(1).max(80),
    }).strict().nullable(),
    // PWA-009 FR-007: aprovada, mas a organizacao da crianca ainda nao usa o app da OSP
    waitingOrganization: z.boolean(),
    // PWA-011: fotos e audio da resposta da crianca (links temporarios). PWA-013: tambem a foto e o audio que o
    // padrinho anexou a propria mensagem; url e expiresAt null = anexo removido (recusada/cancelada ha mais de 30 dias)
    attachments: z.array(z.object({
        id: z.string().uuid(),
        kind: z.enum(['photo', 'audio']),
        url: z.string().url().nullable(),
        expiresAt: z.string().datetime().nullable(),
        // PWA-018 D-08: transcricao ja feita neste app (so audio); ausente = ainda nao transcrito
        transcript: z.object({ text: z.string().max(10_000), language: z.string().min(2).max(10) }).strict().optional(),
    }).strict()).max(4),
}).strict()
export type SponsorThreadMessage = z.infer<typeof sponsorThreadMessageSchema>
export type SponsorMessageAttachment = SponsorThreadMessage['attachments'][number]

export const sponsorThreadDetailSchema = sponsorThreadSummarySchema.omit({ lastMessage: true, lastReceived: true }).extend({
    messages: z.array(sponsorThreadMessageSchema),
    // PWA-013: o padrinho pode anexar foto e audio nesta conversa (pode escrever e o armazenamento esta configurado)
    canAttach: z.boolean(),
    // PWA-018: o botao "Transcrever" aparece nos audios (sessao do proprio padrinho e transcricao ligada no servidor)
    canTranscribe: z.boolean(),
}).strict()
export type SponsorThreadDetail = z.infer<typeof sponsorThreadDetailSchema>

// PWA-009 FR-002: texto de 1 a 2.000 caracteres (obrigatorio tambem com anexos, PWA-013)
export const sponsorSendMessageSchema = z.object({
    body: z.string().trim().min(1).max(2000),
    // PWA-020 D-09: idioma do app no envio (a OSP e o admin traduzem a partir dele)
    locale: sponsorLocaleSchema.optional(),
}).strict()
export type SponsorSendMessage = z.infer<typeof sponsorSendMessageSchema>

/** PWA-013: o que o padrinho pode anexar a uma mensagem. Com anexo, o envio e multipart/form-data com os campos
    `body`, `photo` e `audio`. O servidor confere tipo (pelo conteudo) e tamanho; a duracao e conferida no aparelho. */
export const SPONSOR_ATTACHMENT_LIMITS = {
    photos: 1,
    audios: 1,
    photoBytes: 3 * 1024 * 1024,
    audioBytes: 5 * 1024 * 1024,
    audioSeconds: 120,
    photoMaxSide: 1600,
} as const
export type SponsorOutgoingAttachments = { photo?: Blob | null; audio?: Blob | null }

// ─── Minha conta (tela 14 · RN-29) — espelho do cadastro do CRM, dados do proprio padrinho ─────────

export const sponsorAccountSchema = z.object({
    fullName: z.string().min(1).max(160),
    sponsorSince: z.string().date().nullable(),
    email: z.string().email().nullable(),
    mobile: z.string().max(32).nullable(),
    profession: z.string().max(80).nullable(),
    /* PWA-031: endereco completo. `line1` e a linha unica (rua + numero) que o CRM e as outras telas ja usam; os
       demais campos vem separados quando o CRM ou o padrinho informam. `state` so e UF no Brasil. */
    address: z.object({
        line1: z.string().min(1).max(160),
        city: z.string().min(1).max(80),
        state: z.string().min(1).max(80).nullable(),
        postalCode: z.string().max(20).nullable(),
        street: z.string().max(160).nullable(),
        number: z.string().max(20).nullable(),
        complement: z.string().max(80).nullable(),
        district: z.string().max(80).nullable(),
        country: z.string().regex(/^[A-Z]{2}$/),
    }).strict().nullable(),
    birthDate: z.string().date().nullable(),
    gender: z.enum(['female', 'male', 'other']).nullable(),
    syncedAt: z.string().datetime().nullable(),
    // Spec PWA-007: campos atualizados no app que a equipe ainda vai levar ao cadastro do ChildFund (Dynamics)
    pendingCrmUpdate: z.array(z.enum(['email', 'mobile', 'profession', 'address', 'birthDate', 'gender'])),
}).strict()
export type SponsorAccount = z.infer<typeof sponsorAccountSchema>

// ─── Spec PWA-007: o padrinho atualiza os proprios dados (salvos no app; a equipe leva ao CRM) ───────────

export const SPONSOR_UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR',
    'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'] as const

/** CEP brasileiro: 8 digitos, com ou sem hifen. */
export const BR_POSTAL_CODE = /^\d{5}-?\d{3}$/

const optionalText = (max: number) => z.string().trim().max(max).transform(v => v || null).nullable().optional()

/* PWA-031 D-01/D-02: endereco completo. Pais em codigo ISO de 2 letras (o CRM guarda "BR"). No Brasil, CEP e UF
   obrigatorios; fora, CEP e estado sao texto livre opcional. */
export const sponsorAddressInputSchema = z.object({
    postalCode: optionalText(20),
    street: z.string().trim().min(2).max(140),
    number: z.string().trim().min(1).max(20),
    complement: optionalText(80),
    district: optionalText(80),
    city: z.string().trim().min(2).max(80),
    state: optionalText(80),
    country: z.string().regex(/^[A-Z]{2}$/).default('BR'),
}).strict().superRefine((a, ctx) => {
    if (a.country !== 'BR') return
    if (!a.postalCode || !BR_POSTAL_CODE.test(a.postalCode)) ctx.addIssue({ code: 'custom', path: ['postalCode'], message: 'CEP invalido' })
    if (!a.state || !(SPONSOR_UFS as readonly string[]).includes(a.state)) ctx.addIssue({ code: 'custom', path: ['state'], message: 'UF invalida' })
})
export type SponsorAddressInput = z.input<typeof sponsorAddressInputSchema>

/** Data de nascimento: 18 a 120 anos (D-04). */
export function isAdultBirthDate(value: string, today = new Date()): boolean {
    const [y, m, d] = value.split('-').map(Number)
    if (!y || !m || !d) return false
    const born = new Date(Date.UTC(y, m - 1, d))
    if (born.getUTCFullYear() !== y || born.getUTCMonth() !== m - 1 || born.getUTCDate() !== d) return false
    const age = today.getUTCFullYear() - y - (today.getUTCMonth() + 1 < m || (today.getUTCMonth() + 1 === m && today.getUTCDate() < d) ? 1 : 0)
    return age >= 18 && age <= 120
}

/** Profissao, endereco, nascimento e genero: salvos na hora. E-mail e celular vao por codigo (sponsor-access.schema). */
export const sponsorProfileUpdateSchema = z.object({
    profession: z.string().trim().min(2).max(80).optional(),
    address: sponsorAddressInputSchema.optional(),
    birthDate: z.string().date().refine(v => isAdultBirthDate(v), 'Data de nascimento invalida').optional(),
    gender: z.enum(['female', 'male', 'other']).optional(),
}).strict().refine(v => Object.values(v).some(x => x !== undefined), 'Informe ao menos um campo')
export type SponsorProfileUpdate = z.input<typeof sponsorProfileUpdateSchema>

/** PWA-031 D-03: endereco pelo CEP (ViaCEP, pelo servidor do app). */
export const sponsorPostalCodeLookupSchema = z.object({
    street: z.string().max(160).nullable(),
    district: z.string().max(80).nullable(),
    city: z.string().max(80),
    state: z.enum(SPONSOR_UFS),
}).strict()
export type SponsorPostalCodeLookup = z.infer<typeof sponsorPostalCodeLookupSchema>

// ─── Ajuda e politicas (tela 17 · RN-34/35) — conteudo do tenant ─────────

export const sponsorHelpSchema = z.object({
    faq: z.array(z.object({
        id: z.string().min(1).max(64),
        question: z.string().min(1).max(200),
        answer: z.string().min(1).max(2000),
    }).strict()).max(50),
    documents: z.array(z.object({
        id: z.string().min(1).max(64),
        kind: z.enum(['safeguarding', 'privacy', 'conduct', 'report', 'other']),
        title: z.string().min(1).max(120),
        url: httpsUrlSchema,
        updatedAt: z.string().date().nullable(),
    }).strict()).max(20),
    support: z.object({
        hours: z.string().max(120).nullable(),
        url: httpsUrlSchema.nullable(),
        email: z.string().email().nullable(),
    }).strict(),
}).strict()
export type SponsorHelp = z.infer<typeof sponsorHelpSchema>

// ─── Pedidos LGPD (tela 18 · LGPD Art. 18) — exportar dados / excluir conta ─────────

export const sponsorPrivacyRequestSchema = z.object({
    type: z.enum(['export', 'delete']),
}).strict()
export type SponsorPrivacyRequestInput = z.infer<typeof sponsorPrivacyRequestSchema>

export const sponsorPrivacyRequestResponseSchema = z.object({
    id: z.string().uuid(),
    type: z.enum(['export', 'delete']),
    createdAt: z.string().datetime(),
    // Prazo legal de resposta (dias corridos)
    dueInDays: z.number().int().min(1).max(30),
}).strict()
export type SponsorPrivacyRequestResponse = z.infer<typeof sponsorPrivacyRequestResponseSchema>

// ─── Link identificado (E19) ───────────────────────────────

export const sponsorAccessLinkIssueSchema = z.object({
    crmSponsorRef: z.string().trim().min(1).max(128),
    channel: sponsorOtpChannelSchema,
    source: z.string().trim().max(128).optional(),
}).strict()
export type SponsorAccessLinkIssueInput = z.infer<typeof sponsorAccessLinkIssueSchema>

export const sponsorAccessLinkIssueResponseSchema = z.object({
    id: z.string().uuid(),
    url: httpsUrlSchema,
    expiresAt: z.string().datetime(),
}).strict()
export type SponsorAccessLinkIssueResponse = z.infer<typeof sponsorAccessLinkIssueResponseSchema>

// ─── Auditoria de acesso (E18) ───────────────────────────────

export const SPONSOR_ACCESS_EVENT_TYPES = [
    'otp.requested', 'otp.request_unknown_contact', 'otp.request_ambiguous', 'otp.throttled',
    'otp.delivered', 'otp.delivery_failed', 'otp.verified', 'otp.failed', 'otp.locked',
    'session.created', 'session.revoked', 'session.revoked_all', 'session.expired',
    'link.issued', 'link.redeemed', 'link.rejected', 'link.revoked',
    'child.viewed', 'sponsorship.lost', 'settings.updated',
] as const
export const sponsorAccessEventTypeSchema = z.enum(SPONSOR_ACCESS_EVENT_TYPES)
export type SponsorAccessEventType = z.infer<typeof sponsorAccessEventTypeSchema>

export const sponsorAccessEventOutcomeSchema = z.enum(['success', 'failure', 'blocked', 'info'])
export type SponsorAccessEventOutcome = z.infer<typeof sponsorAccessEventOutcomeSchema>

export const sponsorAccessEventSchema = z.object({
    id: z.string().uuid(),
    eventType: sponsorAccessEventTypeSchema,
    outcome: sponsorAccessEventOutcomeSchema,
    identityId: z.string().uuid().nullable(),
    destinationMasked: z.string().max(64).nullable(),
    childRef: sponsorChildRefSchema.nullable(),
    ipTruncated: z.string().max(45).nullable(),
    uaSummary: z.string().max(80).nullable(),
    metadata: z.record(z.unknown()).nullable(),
    createdAt: z.string().datetime(),
}).strict()
export type SponsorAccessEvent = z.infer<typeof sponsorAccessEventSchema>

export const sponsorAccessEventQuerySchema = z.object({
    identityId: z.string().uuid().optional(),
    eventType: sponsorAccessEventTypeSchema.optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
export type SponsorAccessEventQuery = z.infer<typeof sponsorAccessEventQuerySchema>

// ─── Configuracao do tenant (tenants.settings_json.sponsorApp) — FR-038 ───────────────────────────────

const hostnameSchema = z.string().trim().toLowerCase()
    .regex(/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$|^[a-z0-9-]+\.localhost$/, 'Host invalido')

export const sponsorAppSettingsSchema = z.object({
    enabled: z.boolean().default(false),
    hosts: z.array(hostnameSchema).max(10).default([])
        .refine((hosts) => new Set(hosts).size === hosts.length, 'Hosts duplicados'),
    channels: z.object({
        whatsapp: z.boolean().default(false),
        email: z.boolean().default(true),
    }).strict().default({}),
    crm: z.object({
        connector: z.enum(['http-contract-v1', 'sandbox']).default('sandbox'),
        credentialId: z.string().uuid().optional(),
        baseUrl: httpsUrlSchema.optional(),
        timeoutMs: z.number().int().min(500).max(10_000).default(3000),
    }).strict().default({}),
    whatsapp: z.object({
        provider: z.literal('meta-cloud'),
        credentialId: z.string().uuid(),
        templateName: z.string().trim().min(1).max(512),
        // Idioma do template aprovado no provedor por idioma do app; pt-BR obrigatorio, os demais caem no pt-BR.
        templateLocaleMap: z.object({
            'pt-BR': z.string().min(2).max(10),
            'en-US': z.string().min(2).max(10).optional(),
            'es-ES': z.string().min(2).max(10).optional(),
            'de-DE': z.string().min(2).max(10).optional(),
            'ja-JP': z.string().min(2).max(10).optional(),
        }).strict(),
    }).strict().optional(),
    email: z.object({
        senderName: z.string().trim().max(80).optional(),
        providerScope: z.literal('tenant').default('tenant'),
    }).strict().default({}),
    branding: z.object({
        appName: z.string().trim().min(1).max(45).default('App do Padrinho'),
        shortName: z.string().trim().min(1).max(12).default('Padrinho'),
        themeColor: hexColorSchema.default('#00843D'),
        accentColor: hexColorSchema.default('#FBB21D'),
        backgroundColor: hexColorSchema.default('#F6F3EF'),
        iconStorageKey: z.string().max(512).optional(),
        logoStorageKey: z.string().max(512).optional(),
        logoInverseStorageKey: z.string().max(512).optional(),
        patternStorageKey: z.string().max(512).optional(),
        markStorageKey: z.string().max(512).optional(),
        welcomeImageStorageKey: z.string().max(512).optional(),
    }).strict().default({}),
    legal: z.object({
        privacyNoticeUrl: httpsUrlSchema.optional(),
        dpoContact: z.string().trim().max(254).optional(),
    }).strict().default({}),
    nativeClientsEnabled: z.boolean().default(false),
    auditRetentionDays: z.number().int().min(90).max(1825).default(365),
}).strict()
    .refine((s) => !s.channels.whatsapp || !!s.whatsapp, {
        message: 'Canal WhatsApp habilitado exige configuracao do provedor',
        path: ['whatsapp'],
    })
    .refine((s) => s.crm.connector !== 'http-contract-v1' || (!!s.crm.baseUrl && !!s.crm.credentialId), {
        message: 'Conector http-contract-v1 exige baseUrl e credentialId',
        path: ['crm'],
    })
    .refine((s) => !s.enabled || s.channels.whatsapp || s.channels.email, {
        message: 'App habilitado exige ao menos um canal de codigo',
        path: ['channels'],
    })
export type SponsorAppSettings = z.infer<typeof sponsorAppSettingsSchema>
export type SponsorAppSettingsInput = z.input<typeof sponsorAppSettingsSchema>

// ─── Fila de entrega de codigo (D-12) — codigo em claro so dentro de `enc` ───────────────────────────────

export const sponsorOtpDeliveryJobSchema = z.object({
    challengeId: z.string().uuid(),
    tenantId: z.string().uuid(),
    channel: sponsorOtpChannelSchema,
    // Formato de apps/api/src/lib/encryption.ts: salt:iv:ciphertext:tag (hex, AES-256-GCM) contendo { code, destination }
    enc: z.string().regex(/^[0-9a-f]+:[0-9a-f]+:[0-9a-f]+:[0-9a-f]+$/),
    locale: sponsorLocaleSchema,
}).strict()
export type SponsorOtpDeliveryJob = z.infer<typeof sponsorOtpDeliveryJobSchema>

// ─── Webhook de status do WhatsApp Cloud API (E14) ───────────────────────────────

export const whatsappStatusWebhookSchema = z.object({
    object: z.string().optional(),
    entry: z.array(z.object({
        id: z.string().optional(),
        changes: z.array(z.object({
            field: z.string().optional(),
            value: z.object({
                statuses: z.array(z.object({
                    id: z.string().min(1),
                    status: z.enum(['sent', 'delivered', 'read', 'failed']),
                    timestamp: z.string().regex(/^\d+$/),
                    recipient_id: z.string().optional(),
                    errors: z.array(z.object({
                        code: z.number(),
                        title: z.string().optional(),
                    }).passthrough()).optional(),
                }).passthrough()).optional(),
            }).passthrough(),
        }).passthrough()),
    }).passthrough()),
}).passthrough()
export type WhatsappStatusWebhook = z.infer<typeof whatsappStatusWebhookSchema>
